import { Link } from "react-router-dom";
import { Pct } from "@/components/layernav/primitives";
import { timeAgo } from "@/lib/api";
import { fmtBig, sectorTrack } from "./SectorHero";
import { ProtocolSnapshot } from "./ProtocolSnapshot";
import { SectorLayerIndex } from "./SectorMarketPanel";
import { LiquidityDrivers } from "./LiquidityDrivers";

const LiveChip = ({ pulse }: { pulse: any }) => {
  if (!pulse?.updated_at) return null;
  const coreStale = (pulse.stale || []).some((k: string) =>
    ["tvl", "dex_volume", "stablecoins", "tvl_usd", "dex_volume_24h_usd", "stablecoin_mcap_usd"].includes(k));
  const fresh = Date.now() - new Date(pulse.updated_at).getTime() < 15 * 60 * 1000;
  return fresh && !coreStale ? (
    <span data-testid="defi-pulse-live" className="inline-flex items-center gap-1.5 rounded-full border border-[#34D399]/40 bg-[#34D399]/10 px-2 py-0.5 font-mono text-[9px] font-bold tracking-[0.18em] text-[#34D399]">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#34D399]" />
      LIVE
    </span>
  ) : (
    <span data-testid="defi-pulse-updated" className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
      Updated {timeAgo(pulse.updated_at)}
    </span>
  );
};

const Cell = ({ label, value, delta, testid, title }: {
  label: string; value: string; delta?: number | null; testid: string; title?: string;
}) => (
  <div className="px-4 py-3 sm:px-5 min-w-0" data-testid={testid} title={title}>
    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-gray-500 whitespace-nowrap">{label}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className="font-mono text-lg sm:text-xl font-bold text-white">{value}</span>
      {delta != null && <Pct v={delta} className="!text-[11px]" />}
    </div>
  </div>
);

export const DeFiHero = ({ data }: { data: any }) => {
  const pulse = data?.pulse || {};
  const trending: any[] = data?.trending || [];
  const tvlAsOf = pulse.tvl_as_of
    ? `DeFiLlama daily series · as of ${new Date(pulse.tvl_as_of).toISOString().slice(0, 10)}`
    : undefined;
  return (
    <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="defi-sector-hero">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <img src="/hero/defi-sector-v2.jpg" alt="" fetchPriority="high"
          className="w-full h-full object-cover object-[62%_45%] opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-[#000000]/55 to-[#000000]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-[#000000]/40" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-8">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_440px] gap-8 xl:gap-12 items-stretch">
          <div className="flex flex-col">
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#34D399]">Sector</p>
            <h1 data-testid="defi-masthead"
              className="font-heading font-black text-white text-5xl sm:text-6xl xl:text-7xl tracking-tight mt-1.5">
              DEFI
            </h1>
            <p className="text-gray-300 text-[15px] sm:text-base mt-3 max-w-xl leading-relaxed">
              {data?.sector?.description || "Protocols, liquidity, lending, decentralized markets and the infrastructure powering open finance."}
            </p>

            <div className="mt-6">
              <div className="flex items-center gap-3">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#A78BFA]">DeFi Pulse</p>
                <LiveChip pulse={pulse} />
              </div>
              <div data-testid="defi-pulse"
                className="mt-3 inline-grid grid-cols-2 sm:grid-cols-4 rounded-xl border border-white/[0.09] bg-black/50 backdrop-blur-md divide-x divide-y sm:divide-y-0 divide-white/[0.06] overflow-hidden">
                <Cell testid="pulse-defi-tvl" label="Total DeFi TVL" title={tvlAsOf}
                  value={fmtBig(pulse.tvl_usd)} delta={pulse.tvl_change_24h_pct} />
                <Cell testid="pulse-dex-volume" label="24H DEX Volume"
                  value={fmtBig(pulse.dex_volume_24h_usd)} delta={pulse.dex_volume_change_24h_pct} />
                <Cell testid="pulse-stablecoins" label="Stablecoin Supply"
                  value={fmtBig(pulse.stablecoin_mcap_usd)} delta={pulse.stablecoin_change_24h_pct} />
                <Cell testid="pulse-defi-articles" label="DeFi Stories · 24H"
                  value={pulse.articles_24h != null ? String(pulse.articles_24h) : "—"} />
              </div>
            </div>

            {trending.length > 0 && (
              <div className="mt-5" data-testid="defi-trending">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2.5"
                  title="Topic activity across NewsLayer DeFi coverage, last 48 hours">
                  Top Trending in DeFi <span className="text-gray-600">· 48H</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {trending.map((t) => (
                    <Link key={t.topic} to={`/search?q=${encodeURIComponent(t.label)}`}
                      data-testid={`defi-trending-chip-${t.topic}`}
                      onClick={() => sectorTrack("trending_topic_click", t.topic, "defi")}
                      className="group inline-flex items-center gap-2 rounded-full border border-white/[0.14] bg-white/[0.05] backdrop-blur-sm px-3.5 py-1.5 text-[13px] font-semibold text-gray-200 hover:border-[#34D399]/60 hover:text-white transition-colors">
                      {t.label}
                      <span className="font-mono text-[10px] text-gray-500 group-hover:text-[#34D399] transition-colors">{t.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <LiquidityDrivers pulse={pulse} protocols={data?.protocols?.protocols || []} />
          </div>

          <div className="hidden lg:flex flex-col gap-4">
            <ProtocolSnapshot data={data?.protocols} />
            <SectorLayerIndex testid="defi-layer-index" sector="defi" />
          </div>
        </div>
      </div>
    </section>
  );
};
